import React, { useEffect, useState, useRef, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const InactivityWarning = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const countdownIntervalRef = useRef(null);

  const clearCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const resetModalState = useCallback(() => {
    setIsVisible(false);
    setTimeLeft(120);
    clearCountdown();
  }, [clearCountdown]);

  useEffect(() => {
    const handleWarning = (event) => {
      if (!user) return;
      setIsVisible(true);
      setTimeLeft(event.detail?.timeLeft ?? 120);
    };

    const handleWarningClose = () => {
      resetModalState();
    };

    const handleLogout = () => {
      resetModalState();
    };

    window.addEventListener('inactivityWarning', handleWarning);
    window.addEventListener('inactivityWarningClose', handleWarningClose);
    window.addEventListener('userLogout', handleLogout);

    return () => {
      window.removeEventListener('inactivityWarning', handleWarning);
      window.removeEventListener('inactivityWarningClose', handleWarningClose);
      window.removeEventListener('userLogout', handleLogout);
      clearCountdown();
    };
  }, [user, resetModalState, clearCountdown]);

  useEffect(() => {
    if (!user && isVisible) {
      resetModalState();
    }
  }, [user, isVisible, resetModalState]);

  useEffect(() => {
    if (!isVisible) {
      clearCountdown();
      return;
    }

    clearCountdown();

    countdownIntervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearCountdown();
    };
  }, [isVisible, clearCountdown]);

  const handleStayLoggedIn = () => {
    resetModalState();
    window.dispatchEvent(new Event('resetInactivityTimer'));
  };

  const handleLogoutNow = () => {
    resetModalState();
    logout();
    navigate('/login', { replace: true });
  };

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  if (!isVisible || !user) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-8">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="text-yellow-600" size={32} />
          <h2 className="text-2xl font-bold text-gray-900">Session Expiring</h2>
        </div>

        <p className="text-gray-700 mb-6">
          Your session will expire due to inactivity in:
        </p>

        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6 mb-6 text-center">
          <p className="text-5xl font-bold text-yellow-600 mb-2">
            {String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
          </p>
          <p className="text-sm text-gray-600">minutes remaining</p>
        </div>

        <p className="text-sm text-gray-600 mb-6">
          For security reasons, we automatically log out inactive sessions. Click below to continue.
        </p>

        <div className="flex gap-3">
          <button
            onClick={handleStayLoggedIn}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-semibold transition"
          >
            Stay Logged In
          </button>
          <button
            onClick={handleLogoutNow}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-900 px-4 py-3 rounded-lg font-semibold transition"
          >
            Logout Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default InactivityWarning;